import { Invoice, PaymentTransaction, PaymentTransactionStatus } from '../types';
import { getInvoicePayableAmount, getInvoiceTaxAmount } from './invoiceUtils';

export const SUCCESSFUL_PAYMENT_STATUSES: PaymentTransactionStatus[] = ['SUCCESS', 'RECONCILED'];
export const REFUND_STATUSES: PaymentTransactionStatus[] = ['REFUND_PENDING', 'REFUNDED', 'REFUND_FAILED'];

export const getRazorpayOrderId = (transaction?: PaymentTransaction | null) =>
  transaction?.payload?.razorpay_order_id || transaction?.payload?.verification?.order_id || undefined;

export const getRazorpayPaymentId = (transaction?: PaymentTransaction | null) =>
  transaction?.payload?.razorpay_payment_id || transaction?.gatewayTransactionId || undefined;

export const getRazorpayRefundId = (transaction?: PaymentTransaction | null) =>
  transaction?.payload?.refund?.id || transaction?.payload?.webhook?.refund?.id || undefined;

export const getLatestTransactionByInvoice = (transactions: PaymentTransaction[]) => {
  const map: Record<number, PaymentTransaction> = {};
  transactions.forEach((transaction) => {
    if (!map[transaction.invoiceId]) {
      map[transaction.invoiceId] = transaction;
    }
  });
  return map;
};

export const getPaymentStatusTone = (status: PaymentTransactionStatus) => {
  switch (status) {
    case 'RECONCILED':
    case 'SUCCESS':
      return 'bg-emerald-100 text-emerald-800';
    case 'REFUND_PENDING':
      return 'bg-amber-100 text-amber-800';
    case 'REFUNDED':
      return 'bg-slate-200 text-slate-800';
    case 'REFUND_FAILED':
    case 'FAILED':
      return 'bg-rose-100 text-rose-800';
    default:
      return 'bg-indigo-100 text-indigo-800';
  }
};

export const canRetryInvoicePayment = (invoice: Invoice, latestTransaction?: PaymentTransaction | null) => {
  if (invoice.status === 'Paid') return false;
  if (!latestTransaction) return true;
  return ['FAILED', 'REFUND_FAILED'].includes(latestTransaction.status);
};

export const buildReceiptLabel = (invoiceId: number, transactionId: number) =>
  `invoice-${invoiceId}-payment-${transactionId}-receipt.html`;

export const buildReceiptDocumentHtml = (args: {
  companyName: string;
  invoice: Invoice;
  customerName: string;
  orderLabel: string;
  transaction: PaymentTransaction;
}) => {
  const { companyName, invoice, customerName, orderLabel, transaction } = args;
  const taxAmount = getInvoiceTaxAmount(invoice).toFixed(2);
  const payableAmount = getInvoicePayableAmount(invoice).toFixed(2);
  const paymentId = getRazorpayPaymentId(transaction) || transaction.gatewayTransactionId || `TX-${transaction.id}`;
  const refundId = getRazorpayRefundId(transaction);
  const createdAt = transaction.createdAt.toLocaleString();

  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>Payment Receipt - Invoice #${invoice.id}</title>
  <style>
    body { font-family: Arial, sans-serif; background: #f4f6fb; margin: 0; padding: 24px; color: #0f172a; }
    .sheet { max-width: 820px; margin: 0 auto; background: #ffffff; border: 1px solid #dbe3f1; border-radius: 24px; overflow: hidden; }
    .hero { padding: 28px 32px; background: linear-gradient(135deg, #0f172a, #3730a3); color: #ffffff; }
    .hero h1 { margin: 0 0 8px; font-size: 28px; }
    .hero p { margin: 0; color: rgba(255,255,255,.82); }
    .grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; padding: 24px 32px 0; }
    .card { border: 1px solid #e5e7eb; border-radius: 18px; padding: 18px; background: #fff; }
    .label { font-size: 11px; letter-spacing: .14em; text-transform: uppercase; color: #64748b; font-weight: 700; margin-bottom: 8px; }
    .value { font-size: 16px; font-weight: 700; }
    table { width: calc(100% - 64px); margin: 24px 32px 0; border-collapse: collapse; }
    th, td { padding: 14px 0; border-bottom: 1px solid #e5e7eb; text-align: left; font-size: 14px; }
    th:last-child, td:last-child { text-align: right; }
    .pill { display: inline-block; padding: 6px 10px; border-radius: 999px; background: #d1fae5; color: #065f46; font-size: 12px; font-weight: 700; }
    .footer { padding: 24px 32px 32px; color: #64748b; font-size: 13px; }
    @media print { body { background: #fff; padding: 0; } .sheet { border: 0; border-radius: 0; } }
  </style>
</head>
<body>
  <div class="sheet">
    <div class="hero">
      <h1>Payment Receipt</h1>
      <p>${companyName}</p>
    </div>
    <div class="grid">
      <div class="card">
        <div class="label">Customer</div>
        <div class="value">${customerName}</div>
      </div>
      <div class="card">
        <div class="label">Payment Status</div>
        <div class="value"><span class="pill">${transaction.status}</span></div>
      </div>
      <div class="card">
        <div class="label">Invoice</div>
        <div class="value">#${invoice.id}</div>
        <div>${orderLabel}</div>
      </div>
      <div class="card">
        <div class="label">Gateway Reference</div>
        <div class="value">${paymentId}</div>
        ${refundId ? `<div>Refund: ${refundId}</div>` : ''}
      </div>
    </div>
    <table>
      <thead>
        <tr>
          <th>Description</th>
          <th>Amount</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Invoice subtotal</td>
          <td>Rs. ${invoice.amount.toFixed(2)}</td>
        </tr>
        <tr>
          <td>Tax</td>
          <td>Rs. ${taxAmount}</td>
        </tr>
        <tr>
          <td><strong>Total paid</strong></td>
          <td><strong>Rs. ${payableAmount}</strong></td>
        </tr>
      </tbody>
    </table>
    <div class="footer">
      Receipt generated on ${createdAt}. Keep this document for payment reconciliation and audit purposes.
    </div>
  </div>
</body>
</html>`;
};
